import type {
  FloorLesson,
  FloorStationId,
  FloorTermId,
} from '../content/ar-floor-lessons';

export interface CurriculumSnapshot {
  lesson: FloorLesson | null;
  lessonIndex: number;
  completedLessonIds: readonly string[];
  learnedTerms: readonly FloorTermId[];
  isComplete: boolean;
}

export interface LessonCompletion {
  completedLesson: FloorLesson;
  nextLesson: FloorLesson | null;
  unlockedTerms: readonly FloorTermId[];
  isComplete: boolean;
}

export class TopDownCurriculum {
  private lessonIndex = 0;
  private readonly completedLessonIds: string[] = [];
  private readonly learnedTerms = new Set<FloorTermId>();

  constructor(private readonly lessons: readonly FloorLesson[]) {
    if (lessons.length === 0) {
      this.lessonIndex = -1;
    }
  }

  currentLesson(): FloorLesson | null {
    if (this.lessonIndex < 0) return null;
    return this.lessons[this.lessonIndex] ?? null;
  }

  canUseStation(stationId: FloorStationId): boolean {
    return this.currentLesson()?.stationId === stationId;
  }

  completeCurrent(choiceId: string): LessonCompletion | null {
    const lesson = this.currentLesson();
    if (!lesson) return null;
    const choice = lesson.choices.find((c) => c.id === choiceId);
    if (!choice?.correct) return null;

    const unlocked: FloorTermId[] = [];
    for (const term of lesson.termUnlocks) {
      if (this.learnedTerms.has(term)) continue;
      this.learnedTerms.add(term);
      unlocked.push(term);
    }

    this.completedLessonIds.push(lesson.id);
    this.lessonIndex += 1;
    const nextLesson = this.currentLesson();

    return {
      completedLesson: lesson,
      nextLesson,
      unlockedTerms: unlocked,
      isComplete: nextLesson === null,
    };
  }

  snapshot(): CurriculumSnapshot {
    return {
      lesson: this.currentLesson(),
      lessonIndex: Math.max(0, this.lessonIndex),
      completedLessonIds: [...this.completedLessonIds],
      learnedTerms: [...this.learnedTerms],
      isComplete: this.currentLesson() === null,
    };
  }
}

